import { Cable } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Grid } from '@mui/material';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDialogs } from '@work4all/components';
import { LabeledInput } from '@work4all/components/lib/input/labeled-input';

import { WebDavFspConfig } from '@work4all/models/lib/Classes/WebDavFspConfig.entity';

import { useFormContextPlus } from '../../../../../form-plus/use-form-context-plus';
import { FileServiceProviderConfigFormValue } from '../../FileServiceProviderConfigOverlayController';
import { useFSPAccess } from '../../hooks/use-fsp-access';
import { FailAlert } from '../fail-alert/FailAlert';
import { SuccessAlert } from '../sucess-alert/SuccessAlert';

export const WebDavConf = () => {
  const { t } = useTranslation();
  const { watch, setValue } =
    useFormContextPlus<FileServiceProviderConfigFormValue>();
  const dialogs = useDialogs();

  const config: WebDavFspConfig = watch('config');
  const [loading, setLoading] = useState(false);

  const { testWebDavConfig } = useFSPAccess();

  const handleTestClick = useCallback(async () => {
    setLoading(true);
    try {
      const result = await testWebDavConfig(config);

      dialogs.alert({
        title: t('COMMON.FSP.CONNECTION_TEST.TITLE'),
        description: result.data.testFileServiceProviderConfig ? (
          <SuccessAlert />
        ) : (
          <FailAlert />
        ),
      });
    } catch (err) {
      dialogs.alert({
        title: t('COMMON.FSP.CONNECTION_TEST.TITLE'),
        description: <FailAlert />,
      });
    }
    setLoading(false);
  }, [config, dialogs, t, testWebDavConfig]);

  const handleChange = useCallback(
    (key: keyof WebDavFspConfig, value: string) => {
      setValue(
        'config',
        { ...config, [key]: value },
        { shouldDirty: true, shouldValidate: true }
      );
    },
    [config, setValue]
  );

  return (
    <Grid container spacing="1rem">
      <Grid item xs={12} sm={12} md={12}>
        <LabeledInput
          label={t('COMMON.URL')}
          value={config?.address}
          onChange={(e) => handleChange('address', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6}>
        <LabeledInput
          label={t('COMMON.USERNAME')}
          value={config?.username}
          onChange={(e) => handleChange('username', e.target.value)}
          inputProps={{
            autocomplete: 'new-password',
          }}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6}>
        <LabeledInput
          label={t('COMMON.PASSWORD')}
          value={config?.password}
          type="password"
          onChange={(e) => handleChange('password', e.target.value)}
          inputProps={{
            autocomplete: 'new-password',
          }}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={4}>
        <LoadingButton
          onClick={handleTestClick}
          startIcon={<Cable />}
          loading={loading}
          disabled={!config?.address || !config?.username || !config?.password}
        >
          {t('COMMON.FSP.CONNECTION_TEST.TITLE')}
        </LoadingButton>
      </Grid>
    </Grid>
  );
};
